import classnames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { debounce } from "lodash";
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { AppEventPublisher, StoryBibleType } from "src/AppEventPublisher";
import { createSearchRegex, useSearchableKeyboardShortcut, useTextareaSearchable } from "../../../lib/searchable";
import { useProjectStore } from "../../../stores/projectStore";
import { countWords } from "../../../Utils";
import { formatContent } from "./setFormattedContent";
import GenerateButton from "./GenerateButton";
import { HighlightBackdrop, TextareaContainer, WordCountContainer } from "./styles";

// Helper to escape HTML entities
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function Textarea(
  {
    maxWords,
    help,
    setValue,
    sceneId,
    sceneKey,
    setScenes,
    value,
    onChange,
    height,
    setShowThumbs,
    offsetWordCount,
    hideWordCount,
    onBlur,
    onInput,
    startFocused,
    generate,
    generateDisabled,
    generateDisabledMessage,
    requesting,
    outOfQuota,
    isStreaming,
    setLastInvocation,
    onOpenGuidance,
    setTextAreaFocused,
    abortStream,
    /**
     * When the parent `generate()` already records "Story Bible Generate" (with the same payload
     * as `StoryBibleType.synopsis`), skip the prepended publisher call — otherwise synopsis flows
     * that use `trigger_cta_chat` dual-fire this event twice.
     */
    skipStoryBibleGenerateAnalyticsInvoke,
    _syncing,
    // Global search props
    searchableId,
    searchableName,
    // Filter out custom props that shouldn't be passed to DOM
    onRewriteWithFeedback,
    feedbackValue,
    setFeedbackValue,
    rewritePlaceholder,
    onFocusCallback,
    className,
    ...rest
  },
  ref,
) {
  const setIsEditingTextarea = useProjectStore((state) => state.setIsEditingTextarea);
  const [localWordCount, setLocalWordCount] = useState(0);
  const [isFocused, setIsFocused] = useState(startFocused);
  const [isGenerating, setIsGenerating] = useState(false);
  const containerRef = useRef(null);
  const internalRef = useRef(null);
  const textareaRef = ref || internalRef;

  // Register with global search if searchableId is provided
  const getValue = useCallback(() => value || "", [value]);
  const setValueCallback = useCallback(
    (newValue) => {
      // When Find/Replace sets values, we need to:
      // 1. Update the React state (via setValue or setScenes)
      // 2. Trigger onChange handler to ensure formatting/validation runs
      //    This is critical for proper save behavior - onChange calls setFormattedContent

      // Update state directly first
      if (sceneId && sceneKey && setScenes) {
        setScenes((draft) => {
          if (!draft?.[sceneId]) return;
          draft[sceneId][sceneKey] = newValue;
        });
      } else if (setValue) {
        setValue(newValue);
      }

      // Trigger onChange handler to ensure formatting/validation runs
      // This ensures setFormattedContent is called, which is critical for save behavior
      if (textareaRef.current && onChange) {
        // Create a synthetic event that mimics user input
        const syntheticEvent = {
          target: { value: newValue },
          isTrusted: false, // Mark as programmatic to avoid side effects
        };
        onChange(syntheticEvent);
      } else if (textareaRef.current) {
        // Fallback: update DOM directly and trigger input event
        textareaRef.current.value = newValue;
        const event = new Event("input", { bubbles: true });
        textareaRef.current.dispatchEvent(event);
      }
    },
    [sceneId, sceneKey, setScenes, setValue, onChange, textareaRef],
  );

  const { searchState, fieldId } = useTextareaSearchable(textareaRef, {
    id: searchableId,
    name: searchableName,
    getValue,
    setValue: setValueCallback,
  });

  // Enable Ctrl/Cmd+F to open global find/replace
  useSearchableKeyboardShortcut(containerRef);

  // Track scroll position for highlight sync
  const backdropRef = useRef(null);
  const handleScroll = useCallback(() => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, [textareaRef]);

  // Sync backdrop styles with textarea for pixel-perfect alignment
  const syncBackdropStyles = useCallback(() => {
    if (!backdropRef.current || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const backdrop = backdropRef.current;
    const computed = window.getComputedStyle(textarea);

    // Copy ALL styles that affect text layout
    const stylesToCopy = [
      // Typography
      "fontFamily",
      "fontSize",
      "fontWeight",
      "fontStyle",
      "fontVariant",
      "lineHeight",
      "letterSpacing",
      "wordSpacing",
      "textIndent",
      "textTransform",
      // Box model
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "borderTopWidth",
      "borderRightWidth",
      "borderBottomWidth",
      "borderLeftWidth",
      "marginTop",
      "marginRight",
      "marginBottom",
      "marginLeft",
      "boxSizing",
      // Text wrapping (critical for alignment)
      "whiteSpace",
      "wordBreak",
      "overflowWrap",
      "wordWrap",
    ];

    stylesToCopy.forEach((prop) => {
      backdrop.style[prop] = computed[prop];
    });

    // Force pre-wrap for proper newline handling
    backdrop.style.whiteSpace = "pre-wrap";
    backdrop.style.wordWrap = "break-word";

    // Match exact position and dimensions relative to container
    const rect = textarea.getBoundingClientRect();
    const containerRect = textarea.parentElement.getBoundingClientRect();
    backdrop.style.top = `${rect.top - containerRect.top}px`;
    backdrop.style.left = `${rect.left - containerRect.left}px`;
    backdrop.style.width = `${rect.width}px`;
    backdrop.style.height = `${rect.height}px`;
  }, [textareaRef]);

  const hasHighlights = searchState.searchTerm && value;

  // Generate highlighted text for the backdrop
  const highlightedText = useMemo(() => {
    const text = value || "";
    const { searchTerm, selectedResult } = searchState;

    if (!searchTerm || !text) return text;

    const regex = createSearchRegex(searchTerm, "gi");
    if (!regex) return text;

    // Escape HTML and apply highlights
    let result = "";
    let lastIndex = 0;
    let match;

    // Reset regex state
    regex.lastIndex = 0;

    while ((match = regex.exec(text)) !== null) {
      // Add text before match (escaped)
      result += escapeHtml(text.substring(lastIndex, match.index));

      // Check if this is the selected result
      const isSelected = selectedResult && selectedResult.sourceId === fieldId && selectedResult.from === match.index && selectedResult.to === match.index + match[0].length;

      // Add highlighted match
      const className = isSelected ? "search-highlight search-highlight-selected" : "search-highlight";
      result += `<mark class="${className}">${escapeHtml(match[0])}</mark>`;

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    result += escapeHtml(text.substring(lastIndex));

    // Fix trailing newline issue (as mentioned in the article)
    if (result.endsWith("\n")) {
      result += "\n";
    }

    return result;
  }, [value, searchState, fieldId]);

  useEffect(() => {
    if (!hasHighlights || !textareaRef.current) return;

    // Initial sync (with a small delay to ensure styles are computed)
    requestAnimationFrame(syncBackdropStyles);

    // Watch for size changes (TextareaAutosize resizes dynamically)
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(syncBackdropStyles);
    });
    resizeObserver.observe(textareaRef.current);

    return () => resizeObserver.disconnect();
  }, [hasHighlights, syncBackdropStyles, textareaRef]);

  const updateWordCount = useCallback(
    (text) => {
      const wordCount = countWords(text);
      setLocalWordCount(wordCount);
    },
    [setLocalWordCount],
  );

  const debouncedUpdateWordCount = useMemo(
    () =>
      debounce((text) => {
        updateWordCount(text);
      }, 300),
    [updateWordCount],
  );

  useEffect(() => {
    debouncedUpdateWordCount(value);
  }, [value, debouncedUpdateWordCount]);

  const setFormattedContent = (text) => {
    const { result, action } = formatContent(text, value, maxWords);

    // If rejected (grandfathered over-limit field) or no actual change, skip update
    if (action === "rejected" || result === value) return value;

    if (sceneId) {
      // if sceneId is provided, reach into the scenes state and update the sceneKey

      if (!sceneKey || !setScenes) {
        throw new Error("If sceneId is provided, sceneKey and setScenes must be provided");
      }

      setScenes((draft) => {
        if (!draft?.[sceneId]) return;
        draft[sceneId][sceneKey] = result;
      });
    } else if (setValue) {
      setValue(result);
    }

    return result;
  };

  return (
    <div
      ref={containerRef}
      id={`outer-${rest?.id}`}
    >
      <TextareaContainer $hasHighlights={hasHighlights}>
        {/* Highlight backdrop - positioned behind textarea */}
        {hasHighlights && (
          <HighlightBackdrop
            ref={backdropRef}
            className="highlight-backdrop"
            dangerouslySetInnerHTML={{ __html: highlightedText }}
          />
        )}
        <TextareaAutosize
          {...rest}
          ref={textareaRef}
          className={classnames(height, className)}
          value={value || ""}
          autoFocus={startFocused}
          onChange={(e) => {
            // Fire onInput handler directly to ensure parent toast logic runs
            // reliably regardless of native input event timing
            if (onInput) onInput(e);
            onChange(e);
            const formatted = setFormattedContent(e.target.value) ?? e.target.value;
            debouncedUpdateWordCount(formatted);
            if (e.isTrusted && setShowThumbs) {
              setShowThumbs(false);
            }
          }}
          onScroll={handleScroll}
          onFocus={(e) => {
            if (e.isTrusted) {
              setIsEditingTextarea(true);
              setIsFocused(true);
              setTextAreaFocused && setTextAreaFocused(true);
              onFocusCallback && onFocusCallback(e);
            }
          }}
          onBlur={(e) => {
            setIsEditingTextarea(false);
            const relatedTarget = e.relatedTarget;
            const shouldKeepFocus = Boolean(relatedTarget && containerRef.current && containerRef.current.contains(relatedTarget));

            if (!shouldKeepFocus) {
              setIsFocused(false);
              setTextAreaFocused && setTextAreaFocused(false);
            }

            if (onBlur) {
              onBlur(e);
            }
          }}
        />
        <AnimatePresence mode="popLayout">
          {(isFocused || isGenerating) && generate && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className={`pl-2 mt-[-2.5px] bg-[var(--document-background)]`}
            >
              <GenerateButton
                generate={() => {
                  if (!skipStoryBibleGenerateAnalyticsInvoke) {
                    AppEventPublisher.storyBible.generate(StoryBibleType.synopsis);
                  }
                  generate();
                }}
                generateDisabled={generateDisabled}
                generateDisabledMessage={generateDisabledMessage}
                requesting={requesting}
                outOfQuota={outOfQuota}
                isStreaming={isStreaming}
                setLastInvocation={setLastInvocation}
                abortStream={abortStream}
                setIsGenerating={setIsGenerating}
                requestRewrite={onOpenGuidance}
                value={value}
                type={rest?.id?.split("-")?.[1]}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </TextareaContainer>

      {isFocused && !hideWordCount && (
        <div className={`textarea-info flex flex-row justify-between mt-4 text-[var(--grey-1)] ${!help ? "no-help" : ""}`}>
          <div className="help-text">{help}</div>
          <WordCountContainer offsetWordCount={offsetWordCount}>
            {localWordCount} {maxWords ? `/ ${maxWords}` : ""} words
          </WordCountContainer>
        </div>
      )}
    </div>
  );
}

export default memo(forwardRef(Textarea));
